import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  ManualStandardRequirementItem,
  ManualStandardRequirementItemWithValidation,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { cn } from '@/shared/lib/utils'

interface RequirementItemProps {
  item: ManualStandardRequirementItem
  validationItem?: ManualStandardRequirementItemWithValidation
  isProcessing?: boolean
}

export function RequirementItem({
  item,
  validationItem,
  isProcessing,
}: RequirementItemProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const hasValidation = validationItem !== undefined
  const isVerified = validationItem?.verified ?? false

  return (
    <>
      <div className='min-w-0 flex-1 wrap-break-word'>
        <span
          className={cn(
            'flex gap-2 text-muted-foreground',
            isProcessing && 'text-primary italic'
          )}
        >
          {item.number != null && (
            <span
              className='shrink-0 font-medium text-muted-foreground'
              aria-label='Clause number'
            >
              {item.number}
            </span>
          )}
          <span>{item.text}</span>
        </span>
        {isProcessing && (
          <p className='mt-1 text-xs text-primary italic'>
            {t('standardsPage.manualValidationPage.processing')}
          </p>
        )}
        {hasValidation && !isProcessing && validationItem.explanation && (
          <div
            className={cn(
              'mt-2 overflow-hidden rounded-md border p-2 text-xs italic',
              isVerified
                ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-600 dark:bg-green-950/50 dark:text-green-500'
                : 'border-destructive/30 bg-destructive/10 text-destructive dark:border-destructive/50 dark:bg-destructive/20 dark:text-destructive'
            )}
          >
            {validationItem.explanation}
          </div>
        )}
      </div>

      <span className='flex w-10 shrink-0 items-center justify-center gap-2 font-medium'>
        {isProcessing && (
          <Loader2 className='size-4 animate-spin text-primary' />
        )}
        {hasValidation && !isProcessing && (
          <span
            className={cn(
              'text-lg font-bold',
              isVerified
                ? 'text-green-600 dark:text-green-500'
                : 'text-destructive'
            )}
          >
            {isVerified ? '✓' : '✗'}
          </span>
        )}
      </span>
    </>
  )
}
